// src/Components/Streak/StreakFlame.tsx
// The streak flame: three layered SVG tongues (outer, mid, core) on a soft
// glow, each flickering at its own speed, with ember particles drifting up.
// Size and colour come from the tier (Ember, Blaze, Inferno, Cosmic; Unlit at
// zero) through tokens.css. With prefers-reduced-motion it stands still.
import React, { useId, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { flameScale, tierFor, TierKey } from "./streakModel";

export const FLAME_PATHS = {
  outer:
    "M50 3 C61 24 88 42 86 78 C84 108 68 127 50 127 C32 127 14 110 14 82 C14 60 27 49 33 31 C39 44 42 52 46 57 C50 41 46 21 50 3 Z",
  mid: "M50 36 C58 51 74 63 72 89 C70 111 60 121 50 121 C40 121 28 111 28 93 C28 77 37 69 42 57 C46 66 48 71 50 75 C54 63 50 50 50 36 Z",
  core: "M50 70 C56 79 64 88 62 102 C61 113 56 119 50 119 C44 119 38 113 38 103 C38 91 46 85 50 70 Z",
};

interface Props {
  count: number;
  /** Rendered width in px (the height is 1.3x). */
  size?: number;
  /** Ember particles above the flame. */
  embers?: boolean;
  /** Override the tier (the celebration lights the new tier). */
  tier?: TierKey;
  className?: string;
}

interface Ember {
  left: number;
  drift: number;
  rise: number;
  duration: number;
  delay: number;
  size: number;
}

/** Deterministic pseudo-random embers, so renders do not reshuffle. */
function makeEmbers(n: number): Ember[] {
  const out: Ember[] = [];
  let seed = 7;
  const rnd = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let i = 0; i < n; i++) {
    out.push({
      left: 30 + rnd() * 40,
      drift: (rnd() - 0.5) * 50,
      rise: 0.7 + rnd() * 0.5,
      duration: 1.6 + rnd() * 1.4,
      delay: i * 0.35 + rnd() * 0.3,
      size: 3 + rnd() * 4,
    });
  }
  return out;
}

const StreakFlame: React.FC<Props> = ({ count, size = 120, embers = true, tier, className = "" }) => {
  const reduce = useReducedMotion();
  const uid = useId().replace(/:/g, "");
  const key = tier || tierFor(count).key;
  const scale = flameScale(count);
  const lit = key !== "unlit";
  const sparks = useMemo(() => makeEmbers(key === "cosmic" ? 10 : key === "inferno" ? 9 : key === "blaze" ? 7 : 5), [key]);

  return (
    <div
      className={`streak-flame streak-flame--${key} ${reduce ? "is-still" : ""} ${className}`}
      style={{ ["--flame-w" as any]: `${size}px`, ["--flame-scale" as any]: scale }}
      aria-hidden="true"
    >
      <div className="streak-flame__glow" />
      <svg className="streak-flame__svg" viewBox="0 0 100 130" focusable="false">
        <defs>
          <linearGradient id={`fo${uid}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" style={{ stopColor: "var(--f-mid)" }} />
            <stop offset="100%" style={{ stopColor: "var(--f-outer)" }} />
          </linearGradient>
          <linearGradient id={`fm${uid}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" style={{ stopColor: "var(--f-core)" }} />
            <stop offset="100%" style={{ stopColor: "var(--f-mid)" }} />
          </linearGradient>
          <radialGradient id={`fc${uid}`} cx="50%" cy="70%" r="60%">
            <stop offset="0%" style={{ stopColor: "var(--surface-light)" }} />
            <stop offset="100%" style={{ stopColor: "var(--f-core)" }} />
          </radialGradient>
        </defs>
        <g className="streak-flame__layer streak-flame__layer--outer">
          <path d={FLAME_PATHS.outer} fill={`url(#fo${uid})`} />
        </g>
        <g className="streak-flame__layer streak-flame__layer--mid">
          <path d={FLAME_PATHS.mid} fill={`url(#fm${uid})`} />
        </g>
        {lit && (
          <g className="streak-flame__layer streak-flame__layer--core">
            <path d={FLAME_PATHS.core} fill={`url(#fc${uid})`} />
          </g>
        )}
      </svg>
      {key === "cosmic" && (
        <div className="streak-flame__stars">
          <i /> <i /> <i /> <i />
        </div>
      )}
      {lit && embers && !reduce && (
        <div className="streak-flame__embers">
          {sparks.map((e, i) => (
            <motion.span
              key={i}
              className="streak-flame__ember"
              style={{ left: `${e.left}%`, width: e.size, height: e.size }}
              initial={{ y: 0, x: 0, opacity: 0, scale: 1 }}
              animate={{ y: -size * e.rise, x: e.drift, opacity: [0, 1, 0], scale: [1, 0.9, 0.3] }}
              transition={{ duration: e.duration, delay: e.delay, repeat: Infinity, ease: "easeOut" }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StreakFlame;
